import { Injectable, inject, DOCUMENT } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Config } from '../config/config.service';
import { InternalLanguageKeyMap } from './locale.constants';
import { ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import * as _ from 'lodash-es';
import { ReadyService } from './ready.service';

@Injectable()
export class TranslationService {
  public language: string = 'en';
  private fallbackLanguage: string = 'en';
  private defaultLanguage: string | undefined;
  private supportedLanguages: string[] = [];

  private translocoService = inject(TranslocoService);
  private config = inject(Config);
  public _internalKeyMap = inject(InternalLanguageKeyMap);
  private document: Document = inject(DOCUMENT);
  private readyService = inject(ReadyService);

  onInit(): void {
    this.initializeConfigParameters();
    this.setupLanguages();
  }

  initializeConfigParameters(): void {
    this.defaultLanguage = this.config.CONFIG.defaultLanguage;
    this.supportedLanguages = this.config.CONFIG.supportedLanguages;
  }

  getUrlLanguage(): string | null {
    let languageParameter = this.getParameterByName('lang');
    languageParameter = this.config.CONFIG.languageMapping[String(languageParameter)] || languageParameter;
    if (this.isSupportedLanguage(languageParameter)) {
      return languageParameter;
    }
    return null;
  }

  setupLanguages(): void {
    const fallBackLanguage: string = this.config.CONFIG.fallbackLanguage || this.fallbackLanguage;
    const urlLanguage = this.getUrlLanguage();
    const languageToSet = urlLanguage || this.defaultLanguage || fallBackLanguage;

    this.translocoService.setAvailableLangs(this.supportedLanguages);
    this.setLanguage(languageToSet);
  }

  getLanguage(): string {
    return this.language;
  }

  ready(): ReplaySubject<any> {
    return this.readyService.ready();
  }

  isSupportedLanguage(lang: string | null): boolean {
    return _.includes(this.supportedLanguages, lang);
  }

  setLanguage(lang: string): void {
    const tmpLang: string = this.config.CONFIG.languageMapping[lang] || lang;

    if (this.isSupportedLanguage(tmpLang)) {
      this.language = tmpLang;
      this.translocoService.setActiveLang(tmpLang);
      // setActiveLang() is synchronous and does not trigger the loader.
      // load() explicitly calls getTranslation() so CDN fetch fires and ReadyService is signalled.
      this.translocoService.load(tmpLang).pipe(take(1)).subscribe();
      this.document.documentElement.lang = this.language;
    }
  }

  setUrlLanguage(): void {
    const urlLang: string | null = this.getParameterByName('lang');
    const lang: string = this.config.CONFIG.languageMapping[String(urlLang)] || urlLang;
    if (lang) {
      this.setLanguage(lang);
    }
  }

  getParameterByName = (name: string): string | null => {
    const url = window.location.href;
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return results[2];
  };

  getAcceptLanguageHeader(): string {
    return ((this._internalKeyMap.LOCALES[this.language] || {}) as any).accept_language_header || 'en';
  }

  getDefaultDateFormat(): string {
    return this._internalKeyMap.LOCALES[this.language].date_default;
  }

  getShortDateFormat(): string {
    return this._internalKeyMap.LOCALES[this.language].date_short;
  }

  getLongDateFormat(): string {
    return this._internalKeyMap.LOCALES[this.language].date_long;
  }

  getThousandSeparator(): string {
    return this._internalKeyMap.LOCALES[this.language].thousands_separator;
  }

  getDecimalSeparator(): string {
    return this._internalKeyMap.LOCALES[this.language].decimal_separator;
  }

  getFlagCode(): string {
    return this._internalKeyMap.LOCALES[this.language].flag_code;
  }

  getCurrencyPosition(): string {
    return this._internalKeyMap.LOCALES[this.language].currency_position;
  }
}
